import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  SubHeader,
  QRCode,
  LoadingSpinner,
} from "../StyledComponents/AppStyles";
import {
  LandingSection,
  LandingWrapper,
  LandingText,
  LandingStartArt,
  LandingPendingWrapper,
  LandingQRContainer,
  ArtFiguresOne,
  ArtFiguresTwo,
} from "../StyledComponents/LandingStyles";

function QRCodeDisplay({ connection, invitation, titleElement, styles }) {
  return (
    <LandingQRContainer style={styles}>
      {titleElement}
      {!invitation?.invitation_url || connection ? (
        <div>
          <LoadingSpinner />
        </div>
      ) : (
        <div>
          <QRCode value={invitation.invitation_url} />
        </div>
      )}
    </LandingQRContainer>
  );
}

export default function LandingPage({
  connections,
  checkConnection,
  fetchInvitation,
  invitations,
  requestCredVerification,
  verifiedCredAttributes,
}) {
  const effectRan = useRef(false);
  const navigate = useNavigate();

  const { firstConnection, secondConnection } = connections;
  const { firstInvitation, secondInvitation } = invitations;

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (!effectRan.current) {
        effectRan.current = true;
      } else {
        return () => {
          effectRan.current = false;
        };
      }
    }

    if (Object.keys(firstInvitation).length === 0) {
      fetchInvitation(true);
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(firstInvitation).length !== 0 &&
      Object.keys(secondInvitation).length === 0
    ) {
      checkConnection(true, firstInvitation.invitation_id);
    } else if (Object.keys(secondInvitation).length !== 0) {
      checkConnection(false, secondInvitation.invitation_id);
    }
  }, [invitations]);

  useEffect(() => {
    if (Object.keys(verifiedCredAttributes).length !== 0) {
      return navigate("/account/verified");
    }

    if (firstConnection || secondConnection) {
      if (secondConnection) {
        requestCredVerification(secondInvitation);
      } else {
        requestCredVerification(firstInvitation);
      }
    }
  }, [connections, verifiedCredAttributes]);

  const startingDisplay = (
    <LandingWrapper>
      <div>
        <QRCodeDisplay
          connection={firstConnection}
          invitation={firstInvitation}
          titleElement={
            <h3>
              Scan QR code <br /> to get a quote
            </h3>
          }
          styles={{ width: "30%" }}
        />
        <LandingText>
          <div>
            <h3>Special offer</h3>
            <h1>
              Thank you for choosing Heraldic Insurance as <br /> your{" "}
              <span>#1 Shield</span> against fraud and theft.
            </h1>
            <p>
              For a limited time, Indicio Banking Demo users with Bank Account
              Verifiable Credentials can qualify for discounted quotes for
              Heraldic’s IDShield and CreditShield insurance packages upon
              successful verification of their Bank Account credential.*¹
            </p>
            <p>
              Scan the QR code to begin, or click <u>here</u> to get a quote
              without a Verifiable Credential.²
            </p>
          </div>
          <div>
            <p>
              *This website is part of a demo run by Indicio and is intended to
              showcase the process of verifying a digital credential across
              different web platforms. This website is completely fictitious and
              exists purely for immersive and demonstrational purposes. Indicio
              does not provide insurance or banking services, and is not
              responsible for honoring this special offer.
            </p>
            <p>
              ¹Offer is not valid with any other offers. Insurance premium
              quotes are generated based on a variety of factors and may differ
              depending on the individual. Generated quotes are only valid for a
              period of 7 business days after they are generated. Heraldic
              Insurance does not honor expired quotes.
            </p>
            <p>
              ²Quotes generated without first verifying a Bank Account
              Verifiable Credential do not qualify for this offer.
            </p>
          </div>
        </LandingText>
      </div>
      <LandingStartArt>
        <ArtFiguresOne />
      </LandingStartArt>
    </LandingWrapper>
  );
  const pendingDisplay = (
    <LandingPendingWrapper>
      <div>
        <div>
          <h1>
            Thanks! Next, <span>check your mobile device.</span>
          </h1>
          <p>
            We’ve sent an information request to your wallet app asking you to
            verify your Bank Account Verifiable Credential with us. Once it’s
            verified, you’ll be automatically directed to a web page showing
            your discounted quotes.{" "}
            <a href="https://indicio.tech/contact/">
              Having trouble? Contact us.
            </a>
          </p>
        </div>
        <div>
          <ArtFiguresTwo />
        </div>
      </div>
      <QRCodeDisplay
        connection={secondConnection}
        invitation={secondInvitation}
        titleElement={
          <h3>
            Didn't get a request? <br /> Scan again to resend
          </h3>
        }
        styles={{ width: "34%" }}
      />
    </LandingPendingWrapper>
  );

  return (
    <LandingSection>
      <SubHeader>
        <p>
          Welcome to the Heraldic Insurance portion of Indicio’s Banking
          verifiable credential demo. This is a mock website designed to show
          the process of using a credential across different websites. <br />
          <b>Scan the QR code below with your mobile device</b> to begin this
          part of the demo.
        </p>
      </SubHeader>
      <div>{firstConnection ? pendingDisplay : startingDisplay}</div>
    </LandingSection>
  );
}
